import {
   Component,
   ChangeDetectionStrategy,
   computed,
   inject,
   signal
} from '@angular/core';

import { NgIf, DatePipe } from '@angular/common';
import { ButtonModule } from 'primeng/button';

import { BillingService } from '../../../../services/billing.service';
import { PropertyStoreService } from '../../../../services/stores/property-store.service';
import { PropertyService } from '../../../../services/property.service';
import { PhobosTaskService } from '../../../../services/phobos-task.service';
import { GroupService } from '../../../../services/group.service';
import { User } from '../../../../domain/user';
import { environment } from '../../../../../environments/environment';

import { AccountInformationComponent } from './account-information/account-information.component';
import { ActiveFeaturesAccessComponent } from './active-features-access/active-features-access.component';
import { saveAs } from 'file-saver';

const ACCOUNT_OWNER_ROLE_ID = environment.roles.accountOwner;
const SUPPORT_HUB_LINK = environment.externalLinks.supportHub;

@Component({
   selector: 'app-your-plan',
   standalone: true,
   imports: [
      NgIf,
      ButtonModule,
      AccountInformationComponent,
      ActiveFeaturesAccessComponent,
      AccountInformationComponent
   ],
   templateUrl: './your-plan.component.html',
   styleUrls: ['./your-plan.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class YourPlanComponent {
   // Loading states
   isInfoLoading = signal<boolean>(true);
   isListsLoading = signal<boolean>(true); // left lists + counts
   isAiActivatedTasksLoading = signal<boolean>(true); // right list

   // Services
   private propStore = inject(PropertyStoreService);
   private propService = inject(PropertyService);
   private billing = inject(BillingService);
   private phobos = inject(PhobosTaskService);
   private groupService = inject(GroupService);
   private datePipe = inject(DatePipe);

   supportHubLink = SUPPORT_HUB_LINK;

   // Account Information (top card)
   planName = signal<string>('—');
   commencementDate = signal<string>('—');
   accountOwner = signal<string>('—');
   planDetails = signal<string>('—');
   billingCycle = signal<string>('Monthly on Commencement Date');
   features = signal<string>('—');
   pms = signal<string>('—');
   propertyName = signal<string>('—');
   remainingDaysLeft = signal<number>(0);

   // Active Features & Access (bottom card)
   activeGroups = signal<string[]>([]);
   activeUsers = signal<string[]>([]);
   aiTasksActivated = signal<string[]>([]);
   addOns = signal<string[]>(['Nil']);
   fullUsers = signal<User[]>([]);

   // Counts
   noActiveGroups = computed(() => this.activeGroups().length);
   noActiveUsers = computed(() => this.activeUsers().length);
   aiTasksActivatedCount = computed(() => this.aiTasksActivated().length);

   constructor() {
      this.datePipe = new DatePipe('en-AU');
      this.propStore.currentProperty.subscribe((p) => {
         if (!p?.id) return;
         this.propertyName.set(p.name || p.name || '—');
         this.loadAccountInfo(p.id, p);
         this.loadGroupsAndUsers(p.id);
         this.loadAiTasks(p.id);
      });
   }

   private loadAccountInfo(propId: number, propLike: any): void {
      this.isInfoLoading.set(true);

      this.billing.getPricingTier(propId).subscribe({
         next: (tier) => {
            this.planName.set(tier?.pricingTierName || '—');
            const limit = tier?.groupsLimit ?? null;
            this.planDetails.set(
               typeof limit === 'number' ? `up to ${limit} Groups` : '—'
            );
            this.features.set(tier?.features || '—');
            this.pms.set(
               propLike?.pmsName || propLike?.pms || propLike?.pmsProvider || '—'
            );
            if (tier?.propertyCreationDate) {
               const formatted = this.datePipe.transform(
                  tier.propertyCreationDate,
                  'dd MMMM yyyy'
               );
               this.commencementDate.set(formatted || '—');
            } else {
               this.commencementDate.set('—');
            }

            const cycle = tier?.paymentCycle;
            if (cycle === '12') {
               this.billingCycle.set('Annually on Commencement Date');
            } else if (cycle === '1') {
               this.billingCycle.set('Monthly on Commencement Date');
            } else {
               this.billingCycle.set('—');
            }
            this.remainingDaysLeft.set(tier?.remainingDaysLeft ?? 0);
         },
         error: () => {
            this.planName.set('—');
            this.planDetails.set('—');
            this.features.set('—');
            this.commencementDate.set('—');
            this.pms.set(
               propLike?.pmsName || propLike?.pms || propLike?.pmsProvider || '—'
            );
         }
      });

      this.propService.getUsers(propId).subscribe({
         next: (users: User[]) => {
            const owner =
               users?.find((u) =>
                  (u.roles ?? []).some((r) => r.id === ACCOUNT_OWNER_ROLE_ID)
               ) || null;
            this.accountOwner.set(owner ? this.displayUserName(owner) : '—');
         },
         error: () => this.accountOwner.set('—'),
         complete: () => this.isInfoLoading.set(false)
      });
   }

   private loadGroupsAndUsers(propId: number): void {
      this.isListsLoading.set(true);

      this.groupService.getGroups(propId).subscribe({
         next: (groups) => {
            const names = (groups || [])
               .filter((g) => g?.isActive)
               .map((g) => (g.displayName || g.name || '').trim())
               .filter(Boolean)
               .sort((a, b) => a.localeCompare(b));
            this.activeGroups.set(names);
         },
         error: () => this.activeGroups.set([]),
         complete: () => {
            this.propService.getUsers(propId).subscribe({
               next: (users) => {
                  this.fullUsers.set(users || []);
                  const list = (users || [])
                     .filter((u) => u?.isEnabled)
                     .map((u) => this.displayUserName(u))
                     .filter(Boolean)
                     .sort((a, b) => a.localeCompare(b));
                  this.activeUsers.set(list);
               },
               error: () => this.activeUsers.set([]),
               complete: () => this.isListsLoading.set(false)
            });
         }
      });
   }

   private loadAiTasks(propId: number): void {
      this.isAiActivatedTasksLoading.set(true);
      this.phobos.getTasksByPropertyId(propId).subscribe({
         next: (tasks) => {
            const names = (tasks || [])
               .filter((t) => t?.isEnabledAi)
               .map((t) => t.name?.trim())
               .filter(Boolean)
               .sort((a, b) => a!.localeCompare(b!));
            this.aiTasksActivated.set(names as string[]);
         },
         error: () => this.aiTasksActivated.set([]),
         complete: () => this.isAiActivatedTasksLoading.set(false)
      });
   }

   // --- helpers ---
   private displayUserName(u: User): string {
      return (
         (u.fullName || '').trim() ||
         `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() ||
         u.username ||
         u.email ||
         '—'
      );
   }

   async downloadSummaryXlsx() {
      const ExcelJS = await import('exceljs');

      const prop = (this.propertyName() || 'Property').trim();
      const groups = this.activeGroups();
      const tasks = this.aiTasksActivated();
      const users = this.fullUsers?.() ?? []; // <-- full User[] (see note above)

      const wb = new ExcelJS.Workbook();
      wb.creator = 'Tyalls';
      wb.created = new Date();

      // --- Summary sheet
      const summary = wb.addWorksheet('Summary', {
         properties: { tabColor: { argb: '3E3490' } }
      });
      summary.columns = [
         { header: 'Field', key: 'field', width: 28 },
         { header: 'Value', key: 'value', width: 60 }
      ];
      summary.mergeCells('A1:B1');
      const title = summary.getCell('A1');
      title.value = `Account Summary – ${prop}`;
      title.font = { bold: true, size: 16 };
      title.alignment = { vertical: 'middle' };

      const addSummaryRow = (field: string, value: string | number) =>
         summary.addRow({ field, value });

      addSummaryRow('Generated', new Date().toLocaleString());
      addSummaryRow('Active Groups', groups.length);
      addSummaryRow('Active Users', users.length);
      addSummaryRow('AI Tasks Activated', tasks.length);

      summary.getRow(3).font = { bold: true };
      summary.getRow(3).alignment = { vertical: 'middle' };

      // helper to build simple single-column list sheet
      const buildListSheet = (name: string, items: string[]) => {
         const ws = wb.addWorksheet(name);
         ws.columns = [{ header: 'Name', key: 'name', width: 60 }];

         const header = ws.getRow(1);
         header.font = { bold: true };
         header.alignment = { vertical: 'middle' };

         if (items.length) {
            items.forEach((n) => ws.addRow({ name: n }));
         } else {
            ws.addRow({ name: '— None —' });
            ws.getRow(2).font = { italic: true, color: { argb: '777777' } };
         }

         ws.eachRow((row, rowNumber) => {
            row.eachCell((cell) => {
               cell.border = {
                  top: { style: 'thin', color: { argb: 'DDDDDD' } },
                  left: { style: 'thin', color: { argb: 'DDDDDD' } },
                  bottom: { style: 'thin', color: { argb: 'DDDDDD' } },
                  right: { style: 'thin', color: { argb: 'DDDDDD' } }
               };
            });
            if (rowNumber > 1 && rowNumber % 2 === 0) {
               row.eachCell(
                  (c) =>
                     (c.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'F8FAFC' }
                     })
               );
            }
         });
      };

      // --- Users (detailed) sheet
      const usersWs = wb.addWorksheet('Active Users');
      usersWs.columns = [
         { header: 'Username', key: 'username', width: 28 },
         { header: 'First Name', key: 'firstName', width: 20 },
         { header: 'Last Name', key: 'lastName', width: 20 },
         { header: 'Last Seen', key: 'lastSeen', width: 22 },
         { header: 'Groups', key: 'groups', width: 48 },
         { header: 'Roles', key: 'roles', width: 40 }
      ];
      usersWs.getRow(1).font = { bold: true };

      const safeJoin = (arr?: any[], mapFn?: (x: any) => string) =>
         (arr ?? [])
            .map(mapFn ?? ((x: any) => String(x ?? '')))
            .filter(Boolean)
            .join(', ');

      users.forEach((u) => {
         usersWs.addRow({
            username: u.username || u.email || '—',
            firstName: u.firstName || '',
            lastName: u.lastName || '',
            lastSeen: u.lastSeen ? new Date(u.lastSeen).toLocaleString() : 'Never',
            groups: safeJoin(u.groups, (g) => g?.displayName || g?.name || ''),
            roles: safeJoin(u.roles, (r) => (r?.name || '').replace(/_/g, ' '))
         });
      });

      // Groups & Tasks lists
      buildListSheet('Active Groups', groups);
      buildListSheet('AI Tasks Activated', tasks);

      // Borders + zebra for users
      usersWs.eachRow((row, rowNumber) => {
         row.eachCell((cell) => {
            cell.border = {
               top: { style: 'thin', color: { argb: 'EEEEEE' } },
               left: { style: 'thin', color: { argb: 'EEEEEE' } },
               bottom: { style: 'thin', color: { argb: 'EEEEEE' } },
               right: { style: 'thin', color: { argb: 'EEEEEE' } }
            };
            if (rowNumber > 1 && rowNumber % 2 === 0) {
               cell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: 'FBFBFD' }
               };
            }
         });
      });

      const buffer = await wb.xlsx.writeBuffer();
      const safe = prop.replace(/[^\w\d-_]+/g, '-');
      saveAs(
         new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
         }),
         `Account-Summary-${safe || 'Property'}.xlsx`
      );
   }
}
